import { useState, useRef, useEffect } from "react";
import QuestionnairePage from "../Questionnaire/QuestionnairePage";
import "./QuestionnaireModal.scss";
import MainHeader from "../PublicComponents/MainHeader";
import { Spinner } from "@bphxd/ds-core-react";

type QuestionnaireModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderData: any;
  //token: string | null;
  modalIndex?: number;
};

export default function QuestionnaireModal({
  isOpen,
  onClose,
  orderData,
  //token,
  modalIndex = 0,
}: QuestionnaireModalProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const jswt = sessionStorage.getItem("jwt");
  //console.log("TOKEN IN MODAL:", jswt);
  // ✅ Resizable window state
  const [windowSize, setWindowSize] = useState({ width: 900, height: 700 });
  const [windowPos, setWindowPos] = useState({ x: 100 + modalIndex * 30, y: 50 + modalIndex * 30 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);
 // ✅ HANDLE DRAG MOVE
 useEffect(() => {
  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging) return;
    setWindowPos({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  if (isDragging) {
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
  }

  return () => {
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
  };
}, [isDragging, dragStart]);
  // ✅ HANDLE RESIZE MOVE
  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      setWindowSize((prev) => ({
        width: Math.max(600, prev.width + deltaX),
        height: Math.max(400, prev.height + deltaY),
      }));

      setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [isResizing, dragStart]);
  if (!isOpen) return null;

  const baseZIndex = 999 + modalIndex;

  // ✅ HANDLE DRAG START
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.modal-controls')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - windowPos.x,
      y: e.clientY - windowPos.y,
    });
  };

 

  // ✅ HANDLE RESIZE START
  const handleResizeStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };


  // ✅ HANDLE SUBMIT FROM QUESTIONNAIRE PAGE
  const handleSubmissionStart = () => {
    setIsSubmitting(true);
  };

  const handleSubmissionSuccess = () => {
    setSubmitSuccess(true);
    setTimeout(() => {
      setSubmitSuccess(false);
      setIsSubmitting(false);
      onClose(); // ✅ Close modal after success
    }, 2000);
  };

  const handleSubmissionError = () => {
    setIsSubmitting(false);
  };

  return (
    <>
      {/* FULL SCREEN MODE */}
      {isFullScreen && (
        <div className="modal-full-screen" style={{ zIndex: baseZIndex + 100 }}>
          <div className="modal-header-bar">
            <h2>Questionnaire - {orderData?.order_number} || Country - {orderData?.country_name}</h2>
            <div className="modal-controls">
              <button 
                onClick={() => setIsFullScreen(false)} 
                className="control-btn restore-btn"
                title="Restore"
              >
                ▢
              </button>
              <button 
                onClick={onClose} 
                className="control-btn close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
          <div className="modal-body">
            <MainHeader />
            <QuestionnairePage 
              initialData={orderData} 
              //token={jswt}
              onSubmittingStart={handleSubmissionStart}
              onSubmitSuccess={handleSubmissionSuccess}
              onSubmitError={handleSubmissionError}
            />
          </div>

          {/* ✅ LOADING OVERLAY */}
          {isSubmitting && !submitSuccess && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '8px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
               <Spinner color="brand"/>
                <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>
                  Submitting your questionnaire...
                </p>
              </div>
            </div>
          )}

          {/* ✅ SUCCESS MESSAGE */}
          {submitSuccess && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}>
              <div style={{
                backgroundColor: 'white',
                padding: '40px',
                borderRadius: '8px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxWidth: '400px',
                animation: 'slideIn 0.3s ease-out'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                <h2 style={{ color: '#2e7d32', margin: 0, marginBottom: '10px' }}>Success!</h2>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  Your questionnaire has been submitted successfully.
                </p>
                <p style={{ color: '#999', fontSize: '14px' }}>
                  This window will close automatically...
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ✅ MINIMIZED MODE - LEFT TO RIGHT LAYOUT */}
      {isMinimized && !isFullScreen && (
        <div 
          className="modal-minimized" 
          style={{ 
            zIndex: baseZIndex + 50, 
            right: `${20 + modalIndex * 260}px`,
            bottom: '20px',
            position: 'fixed'
          }}
        >
          <div className="modal-minimized-header">
            <span className="minimized-title">
              📋 {orderData?.order_number} - {orderData?.country_name}
            </span>
            <div className="minimized-controls">
              <button 
                onClick={() => setIsMinimized(false)} 
                className="control-btn restore-btn"
                title="Restore"
              >
                ⬜
              </button>
              <button 
                onClick={onClose} 
                className="control-btn close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ NORMAL WINDOWED MODE - RESIZABLE & DRAGGABLE */}
      {!isMinimized && !isFullScreen && (
        <div 
          className="modal-overlay" 
          style={{ zIndex: baseZIndex }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div 
            ref={modalRef}
            className="modal-window" 
            onClick={(e) => e.stopPropagation()} 
            style={{
              position: 'fixed',
              left: `${windowPos.x}px`,
              top: `${windowPos.y}px`,
              width: `${windowSize.width}px`,
              height: `${windowSize.height}px`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              backgroundColor: 'white',
              margin: 0
            }}
          >
            {/* ✅ DRAGGABLE HEADER */}
            <div 
              className="modal-header-bar"
              onMouseDown={handleDragStart}
              style={{ 
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
                padding: '12px 16px',
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                  {orderData?.order_number} - {orderData?.country_name}
                </h2>
                <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
                  ({windowSize.width}x{windowSize.height}px)
                </span>
              </div>
              <div className="modal-controls" style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => setIsFullScreen(true)} 
                  className="control-btn fullscreen-btn"
                  title="Fullscreen"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#e0e0e0')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  ⛶
                </button>
                <button 
                  onClick={() => setIsMinimized(true)} 
                  className="control-btn minimize-btn"
                  title="Minimize"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#e0e0e0')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  −
                </button>
                <button 
                  onClick={onClose} 
                  className="control-btn close-btn"
                  title="Close"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background 0.2s',
                    color: '#d32f2f'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#ffebee')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* ✅ SCROLLABLE CONTENT */}
            <div 
              className="modal-body" 
              style={{ 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '16px',
                backgroundColor: 'white'
              }}
            >
              <MainHeader />
              <QuestionnairePage 
                initialData={orderData} 
                //token={jswt}
                onSubmittingStart={handleSubmissionStart}
                onSubmitSuccess={handleSubmissionSuccess}
                onSubmitError={handleSubmissionError}
              />
            </div>

            {/* ✅ RESIZE HANDLE - Bottom Right Corner */}
            <div
              onMouseDown={handleResizeStart}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '20px',
                height: '20px',
                cursor: 'se-resize',
                backgroundColor: '#ddd',
                borderRadius: '0 0 8px 0',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.backgroundColor = '#999';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.5';
                e.currentTarget.style.backgroundColor = '#ddd';
              }}
              title="Drag to resize"
            />

            {/* ✅ LOADING OVERLAY */}
            {isSubmitting && !submitSuccess && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                borderRadius: '8px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '40px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  <Spinner color="brand"/>
                  <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>
                    Submitting your questionnaire...
                  </p>
                </div>
              </div>
            )}

            {/* ✅ SUCCESS MESSAGE */}
            {submitSuccess && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                borderRadius: '8px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '40px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  maxWidth: '400px',
                  animation: 'slideIn 0.3s ease-out'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                  <h2 style={{ color: '#2e7d32', margin: 0, marginBottom: '10px' }}>Success!</h2>
                  <p style={{ color: '#666', marginBottom: '20px' }}>
                    Your questionnaire has been submitted successfully.
                  </p>
                  <p style={{ color: '#999', fontSize: '14px' }}>
                    This window will close automatically...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}